/**
 * BasicInfoStep - Wizard step 1 for basic monitor information
 *
 * Collects:
 * - Monitor name (required, 3-100 characters)
 * - Description (optional)
 * - Target dataset ID (required)
 */

import React, { useState } from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle } from 'lucide-react';
import { useMonitorCreationStore } from '@/features/monitors/store/monitorCreationStore';
import { DatasetCombobox } from '@/components/datasets/DatasetCombobox';

/**
 * BasicInfoStep Component
 *
 * First step of monitor creation wizard for collecting basic information.
 *
 * @example
 * ```tsx
 * <BasicInfoStep />
 * ```
 */
export const BasicInfoStep: React.FC = () => {
  const store = useMonitorCreationStore();

  /**
   * Validate monitor name
   * Must start with a letter and contain only alphanumeric characters and underscores
   *
   * Declared as a hoisted function (not a const arrow) so it can be referenced
   * by the lazy useState initializer below, which runs before this line is
   * reached in render order.
   */
  function validateName(name: string): string {
    if (!name) {
      return 'Monitor name is required';
    }
    if (name.length < 3) {
      return 'Monitor name must be at least 3 characters';
    }
    if (name.length > 100) {
      return 'Monitor name must not exceed 100 characters';
    }
    // Unified validation pattern: must start with a letter, only letters, numbers, and underscores
    if (!/^[a-zA-Z][a-zA-Z0-9_]*$/.test(name)) {
      return 'Monitor name must start with a letter and contain only letters, numbers, and underscores (no hyphens or spaces)';
    }
    return '';
  }

  /**
   * Seed the initial error from whatever name is already in the store (e.g.
   * the user navigated back to this step). Computed once via a lazy
   * initializer instead of a mount-only effect, since there's nothing here
   * that needs to re-run after the first render.
   */
  const [nameError, setNameError] = useState<string>(() =>
    store.name ? validateName(store.name) : ''
  );

  /**
   * Handle name change with validation
   */
  const handleNameChange = (value: string) => {
    store.setName(value);
    const error = validateName(value);
    setNameError(error);
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Monitor Information</CardTitle>
          <CardDescription>
            Provide a name and description for this directory monitor
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Monitor Name */}
          <div className="space-y-2">
            <Label htmlFor="monitor-name">
              Monitor Name <span className="text-destructive">*</span>
            </Label>
            <Input
              id="monitor-name"
              value={store.name}
              onChange={(e) => handleNameChange(e.target.value)}
              placeholder="e.g., Incoming_Files_Monitor"
              className={nameError ? 'border-destructive' : ''}
              maxLength={100}
            />
            {nameError && (
              <p className="text-sm text-destructive flex items-center gap-1">
                <AlertCircle className="h-4 w-4" />
                {nameError}
              </p>
            )}
            <p className="text-sm text-muted-foreground">
              Must start with a letter. Only letters, numbers, and underscores allowed ({store.name.length}/100 characters)
            </p>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="monitor-description">Description</Label>
            <Textarea
              id="monitor-description"
              value={store.description}
              onChange={(e) => store.setDescription(e.target.value)}
              placeholder="Optional description of what this monitor does..."
              rows={3}
              maxLength={500}
            />
            <p className="text-sm text-muted-foreground">
              Provide additional context about this monitor's purpose ({store.description.length}/500 characters)
            </p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Target Dataset</CardTitle>
          <CardDescription>
            Select the dataset where discovered files will be ingested
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Dataset Selection */}
          <div className="space-y-2">
            <Label htmlFor="dataset-select">
              Dataset <span className="text-destructive">*</span>
            </Label>

            <DatasetCombobox
              id="dataset-select"
              value={store.dataset_id || null}
              onChange={(datasetId) => store.setDatasetId(datasetId ?? '')}
            />
            <p className="text-sm text-muted-foreground">
              Files discovered by this monitor will be ingested into the selected dataset
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Validation Summary */}
      {(nameError || !store.dataset_id) && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Please complete all required fields to continue:
            <ul className="list-disc list-inside mt-2">
              {nameError && <li>{nameError}</li>}
              {!store.dataset_id && <li>Select a target dataset</li>}
            </ul>
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
};
